// Inspector panels for the v2.58 funnel blocks (nav, banner, testimonials,
// modules, bio, offer) and the shared funnel fields (highlight words, proof
// line, layout variants, band tones, anchors). SectionFields delegates here so
// its own switch stays readable. The building blocks (labeled fields,
// segmented choices, groups, item cards) live in InspectorKit. Like
// SectionFields, each panel opens with an always-open "Content" group; Media
// and Button groups fold.
import type {
  FooterLink, MarkStyle, ModuleItem, OfferItem, QuoteItem, Section, SectionBg, SectionType, SectionVariant, StatItem,
} from '../model/pages';
import { PAGE_ICON_NAMES } from '../render/sections/icons';
import { etWallToIso, isoToEtWall } from '../model/pages';
import ImageInput from './ImageInput';
import {
  Block, Check, Choice, HINT, ItemList, LinkList, SelectField, StringList, TextAreaField, TextField, fileLabel,
  type ChoiceOptions,
} from './InspectorKit';

type On = (p: Partial<Section>) => void;

/** Placeholder for fields a section works fine without. */
const OPTIONAL = 'Optional';

/** A labeled segmented choice. Kept for older callers; new code uses Choice
 *  from the inspector kit. */
export function ChoiceRow<T extends string>({ label, value, options, onChange }: {
  label: string; value: T; options: ChoiceOptions<T>; onChange: (v: T) => void;
}) {
  return <Choice label={label} value={value} options={options} onChange={onChange} />;
}

/** Layout variants per section type. 'default' is stored as undefined. */
export const VARIANTS: Partial<Record<SectionType, readonly (readonly [SectionVariant | 'default', string])[]>> = {
  stats: [['default', 'Plain'], ['cards', 'Cards'], ['divided', 'Divided']],
  features: [['default', 'Cards'], ['media', 'Image cards'], ['plain', 'Plain'], ['list', 'List']],
  faq: [['default', 'List'], ['two-col', 'Two columns'], ['cards', 'Cards']],
  steps: [['default', 'Columns'], ['cards', 'Cards'], ['timeline', 'Timeline']],
  benefits: [['default', 'List'], ['grid', 'Two columns'], ['cards', 'Cards']],
  testimonial: [['default', 'Card'], ['center', 'Big quote']],
  testimonials: [['default', 'Grid'], ['wall', 'Wall'], ['list', 'Rows']],
  modules: [['default', 'List'], ['grid', 'Grid']],
  bio: [['default', 'Photo and story'], ['compact', 'Byline']],
};

export function VariantPicker({ section, onChange }: { section: Section; onChange: On }) {
  const opts = VARIANTS[section.type];
  if (!opts) return null;
  return (
    <Choice label="Layout" value={(section.variant ?? 'default') as SectionVariant | 'default'} options={opts}
      onChange={(v) => onChange({ variant: v === 'default' ? undefined : v })} />
  );
}

const MARKS: ChoiceOptions<MarkStyle | 'default'> = [
  ['default', 'Style default'], ['color', 'Color'], ['marker', 'Marker'], ['box', 'Box'], ['italic', 'Italic'], ['underline', 'Underline'],
];

/** Highlight words inside the heading, and how they render. */
export function HighlightField({ section, onChange, field = 'heading' }: { section: Section; onChange: On; field?: 'heading' | 'headline' }) {
  const heading = (field === 'headline' ? section.headline : section.heading) ?? '';
  const missing = (section.highlight ?? '').split('|').map((p) => p.trim()).filter(Boolean).filter((p) => !heading.includes(p));
  return (
    <>
      <TextField label="Highlight words" placeholder="e.g. five-star | 30 days" value={section.highlight ?? ''}
        error={missing.length > 0 ? `Not in the ${field}: ${missing.join(', ')}` : undefined}
        onChange={(v) => onChange({ highlight: v || undefined })} />
      {section.highlight && (
        <Choice label="Highlight look" value={section.mark ?? 'default'} options={MARKS}
          onChange={(v) => onChange({ mark: v === 'default' ? undefined : v })} />
      )}
    </>
  );
}

/** Small reassurance line under the button. */
export function NoteField({ section, onChange, placeholder = OPTIONAL }: { section: Section; onChange: On; placeholder?: string }) {
  return (
    <TextField dynamic label="Note under the button" placeholder={placeholder} value={section.cta_note ?? ''}
      onChange={(v) => onChange({ cta_note: v || undefined })} />
  );
}

const RATINGS: ChoiceOptions<string> = [['0', 'None'], ['5', '5 stars'], ['4', '4 stars']];

/** Avatar stack + stars + a short proof line. The caller puts it in a
 *  "Social proof" block. */
export function ProofFields({ section, onChange }: { section: Section; onChange: On }) {
  const avatars = section.proof_avatars ?? [];
  return (
    <>
      <TextField dynamic label="Proof text" placeholder={OPTIONAL} value={section.proof_text ?? ''}
        onChange={(v) => onChange({ proof_text: v || undefined })} />
      {section.type === 'hero' && (
        <Check label="Show above the headline" checked={!!section.proof_above} onChange={(v) => onChange({ proof_above: v || undefined })} />
      )}
      <Choice label="Stars" value={String(section.rating ?? 0)} options={RATINGS} onChange={(v) => onChange({ rating: Number(v) || undefined })} />
      <ItemList label="Avatars" noun="avatar" items={avatars} max={5} make={() => ''}
        title={() => undefined} summary={(url) => fileLabel(url)} thumb={(url) => url}
        onChange={(a) => onChange({ proof_avatars: a })}>
        {(url, _i, set) => <ImageInput label="Photo" value={url} clearable={false} onChange={set} />}
      </ItemList>
    </>
  );
}

/** Background band choice. The modern tones lead; a classic tone shows only
 *  while a section still uses it, so old pages keep a way to see their value
 *  (with it the list has 5 options and becomes a select). */
export function BandPicker({ value, onChange }: { value: SectionBg | undefined; onChange: (bg: SectionBg) => void }) {
  const cur = value ?? 'default';
  const opts: [SectionBg, string][] = [['default', 'Default'], ['tint', 'Soft'], ['ink', 'Dark'], ['brand', 'Brand']];
  for (const [legacy, lbl] of [['light', 'Light (classic)'], ['dark', 'Dark (classic)'], ['bold', 'Bold (classic)']] as const) {
    if (cur === legacy) opts.push([legacy, lbl]);
  }
  return <Choice label="Background" value={cur} options={opts} onChange={onChange} />;
}

/** Paint the band as a rounded panel inside the page margins (modern styles). */
export function InsetField({ section, onChange }: { section: Section; onChange: On }) {
  if (!section.bg || section.bg === 'default') return null;
  return <Check label="Rounded panel (modern styles)" checked={!!section.inset} onChange={(v) => onChange({ inset: v || undefined })} />;
}

/** In-page anchor so a top-bar link (#pricing) can scroll to this section. */
export function AnchorField({ section, onChange }: { section: Section; onChange: On }) {
  return (
    <TextField label="Anchor" placeholder="e.g. pricing" value={section.anchor ?? ''}
      hint={section.anchor ? `Link to it with #${section.anchor}` : 'Lets a link like #pricing jump here.'}
      onChange={(v) => onChange({ anchor: v.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+/, '') || undefined })} />
  );
}

/** Feature icon: a built-in line icon, or none (the text mark / image fields apply). */
export function IconSelect({ value, onChange }: { value: string | undefined; onChange: (name: string | undefined) => void }) {
  return (
    <SelectField label="Icon" value={value ?? ''} onChange={(v) => onChange(v || undefined)}>
      <option value="">No icon</option>
      {PAGE_ICON_NAMES.map((n) => <option key={n} value={n}>{n.charAt(0).toUpperCase() + n.slice(1).replace(/[-_]/g, ' ')}</option>)}
    </SelectField>
  );
}

export function AttributionForwardingField({ section, onChange }: { section: Section; onChange: On }) {
  return <div className="grid min-w-0 gap-1.5">
    <Check label="Pass ad tags to this button’s destination" checked={section.forward_attribution === true} onChange={(v) => onChange({ forward_attribution: v || undefined })} />
    <p className={HINT}>Use only for your own marketing form or booking destination. Existing destination tags stay unchanged.</p>
  </div>;
}

function ButtonFields({ section, onChange, labelLabel = 'Button text', linkLabel = 'When clicked, go to' }: {
  section: Section; onChange: On; labelLabel?: string; linkLabel?: string;
}) {
  return (
    <>
      <TextField dynamic label={labelLabel} value={section.label ?? ''} onChange={(v) => onChange({ label: v })} />
      <TextField label={linkLabel} placeholder="#form, #book, #next or a URL" value={section.href ?? ''} onChange={(v) => onChange({ href: v })} />
      <AttributionForwardingField section={section} onChange={onChange} />
    </>
  );
}

export function NavFields({ section, onChange }: { section: Section; onChange: On }) {
  return (
    <>
      <Block name="Content" collapsible={false}>
        <Check label="Show the page logo" checked={section.logo !== false} onChange={(v) => onChange({ logo: v })} />
        <TextField dynamic label="Brand name" placeholder="Shows when there is no logo" value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
        <LinkList dynamic label="Links (desktop)" links={section.links ?? []} newHref="#" labelPlaceholder="e.g. Pricing"
          hrefPlaceholder="#pricing or https://…" onChange={(links: FooterLink[]) => onChange({ links })} />
        <p className={HINT}>To link to a section, give it an anchor under Style.</p>
      </Block>
      <Block name="Button" defaultOpen>
        <ButtonFields section={section} onChange={onChange} />
      </Block>
    </>
  );
}

export function BannerFields({ section, onChange }: { section: Section; onChange: On }) {
  return (
    <>
      <Block name="Content" collapsible={false}>
        <TextField dynamic label="Announcement" value={section.body ?? ''} onChange={(v) => onChange({ body: v })} />
        <TextField label="Countdown to (Eastern time)" type="datetime-local" value={isoToEtWall(section.deadline)}
          onChange={(v) => onChange({ deadline: v ? etWallToIso(v) : undefined })} />
      </Block>
      <Block name="Button" defaultOpen>
        <ButtonFields section={section} onChange={onChange} labelLabel="Link text" />
      </Block>
    </>
  );
}

/** The review wall's reviews. The caller puts it in the Content block. */
export function ReviewsFields({ section, onChange }: { section: Section; onChange: On }) {
  return (
    <ItemList<QuoteItem> label="Reviews" noun="review" items={section.quotes ?? []}
      make={() => ({ quote: '', name: '', rating: 5 })}
      title={(q) => q.name} summary={(q) => q.quote} thumb={(q) => q.photo_url}
      onChange={(quotes) => onChange({ quotes })}>
      {(q, _i, set) => {
        const p = (x: Partial<QuoteItem>) => set({ ...q, ...x });
        return (
          <>
            <TextAreaField dynamic label="Review" value={q.quote} onChange={(v) => p({ quote: v })} />
            <div className="grid min-w-0 grid-cols-2 gap-2">
              <TextField dynamic label="Name" value={q.name} onChange={(v) => p({ name: v })} />
              <TextField dynamic label="Role" placeholder="e.g. Founder" value={q.role ?? ''} onChange={(v) => p({ role: v })} />
            </div>
            <Choice label="Stars" value={String(q.rating ?? 0)} options={RATINGS} onChange={(v) => p({ rating: Number(v) || undefined })} />
            <ImageInput label="Photo" value={q.photo_url ?? ''} onChange={(url) => p({ photo_url: url })} />
            <ImageInput label="Screenshot or video cover" value={q.image ?? ''} onChange={(url) => p({ image: url || undefined })} />
            {q.image && (
              <TextField label="Video link" placeholder="Plays on the cover" value={q.video_url ?? ''}
                onChange={(v) => p({ video_url: v || undefined })} />
            )}
          </>
        );
      }}
    </ItemList>
  );
}

/** The curriculum's modules. The caller puts it in the Content block. */
export function ModulesFields({ section, onChange }: { section: Section; onChange: On }) {
  const mods = section.modules ?? [];
  return (
    <ItemList<ModuleItem> label="Modules" noun="module" items={mods}
      make={() => ({ label: `Module ${mods.length + 1}`, title: '' })}
      title={(m) => m.title} summary={(m) => m.label}
      onChange={(modules) => onChange({ modules })}>
      {(m, _i, set) => {
        const p = (x: Partial<ModuleItem>) => set({ ...m, ...x });
        return (
          <>
            <div className="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] gap-2">
              <TextField dynamic label="Label" placeholder="Module 1" value={m.label ?? ''} onChange={(v) => p({ label: v })} />
              <TextField dynamic label="Title" value={m.title} onChange={(v) => p({ title: v })} />
            </div>
            <TextAreaField dynamic label="What it covers" value={m.body ?? ''} onChange={(v) => p({ body: v })} />
            <TextAreaField dynamic label="Lessons" placeholder="One per line" value={(m.lessons ?? []).join('\n')}
              onChange={(v) => p({ lessons: v.split('\n') })} />
            <ImageInput label="Image" value={m.image ?? ''} onChange={(url) => p({ image: url })} />
          </>
        );
      }}
    </ItemList>
  );
}

/** Number and label pairs ("10k+" / "happy customers") as expandable rows:
 *  the stats bar's stats and the bio's numbers. */
export function NumberRows({ label, noun, items, onChange }: { label: string; noun: string; items: StatItem[]; onChange: (a: StatItem[]) => void }) {
  return (
    <ItemList<StatItem> label={label} noun={noun} items={items} make={() => ({ value: '', label: '' })}
      title={(st) => st.value} summary={(st) => st.label} onChange={onChange}>
      {(st, _i, set) => (
        <div className="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] gap-2">
          <TextField dynamic label="Number" placeholder="e.g. 10k+" value={st.value} onChange={(v) => set({ ...st, value: v })} />
          <TextField dynamic label="Label" placeholder="e.g. happy customers" value={st.label} onChange={(v) => set({ ...st, label: v })} />
        </div>
      )}
    </ItemList>
  );
}

export function BioFields({ section, onChange }: { section: Section; onChange: On }) {
  return (
    <>
      <Block name="Content" collapsible={false}>
        <VariantPicker section={section} onChange={onChange} />
        <TextField dynamic label="Small label above" placeholder={OPTIONAL} value={section.eyebrow ?? ''} onChange={(v) => onChange({ eyebrow: v })} />
        <TextAreaField dynamic singleLine label="Heading" value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
        <HighlightField section={section} onChange={onChange} />
        <TextAreaField dynamic label="Your story" inputClassName="min-h-[96px]" value={section.body ?? ''} onChange={(v) => onChange({ body: v })} />
        <StringList dynamic label="Credentials" noun="credential" placeholder="e.g. 12 years in the field" items={section.items ?? []}
          onChange={(items) => onChange({ items })} />
        <NumberRows label="Numbers" noun="number" items={section.stats ?? []} onChange={(stats) => onChange({ stats })} />
        <div className="grid min-w-0 grid-cols-2 gap-2">
          <TextField dynamic label="Sign-off name" value={section.attribution ?? ''} onChange={(v) => onChange({ attribution: v })} />
          <TextField dynamic label="Title" value={section.role ?? ''} onChange={(v) => onChange({ role: v })} />
        </div>
      </Block>
      <Block name="Media" defaultOpen>
        <ImageInput label="Photo" value={section.photo_url ?? ''} onChange={(url) => onChange({ photo_url: url })} />
        <Choice label="Photo side" value={section.image_side ?? 'left'} options={[['left', 'Left'], ['right', 'Right']] as const}
          onChange={(v) => onChange({ image_side: v })} />
      </Block>
    </>
  );
}

export function OfferFields({ section, onChange }: { section: Section; onChange: On }) {
  return (
    <>
      <Block name="Content" collapsible={false}>
        <TextField dynamic label="Small label above" placeholder={OPTIONAL} value={section.eyebrow ?? ''} onChange={(v) => onChange({ eyebrow: v })} />
        <TextAreaField dynamic singleLine label="Heading" placeholder="e.g. Everything you get" value={section.heading ?? ''} onChange={(v) => onChange({ heading: v })} />
        <HighlightField section={section} onChange={onChange} />
        <TextAreaField dynamic label="Supporting text" placeholder={OPTIONAL} value={section.subheadline ?? ''} onChange={(v) => onChange({ subheadline: v })} />
        <ItemList<OfferItem> label="What is included" noun="item" items={section.offer_items ?? []} make={() => ({ label: '' })}
          title={(o) => o.label} summary={(o) => o.value} onChange={(offer_items) => onChange({ offer_items })}>
          {(o, _i, set) => (
            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_7.5rem] gap-2">
              <TextField dynamic label="Item" value={o.label} onChange={(v) => set({ ...o, label: v })} />
              <TextField dynamic label="Value" placeholder="$97 value" value={o.value ?? ''} onChange={(v) => set({ ...o, value: v })} />
            </div>
          )}
        </ItemList>
        <div className="grid min-w-0 grid-cols-2 gap-2">
          <TextField dynamic label="Total value" placeholder="e.g. $1,240" value={section.price_compare ?? ''} onChange={(v) => onChange({ price_compare: v })} />
          <TextField dynamic label="Price" placeholder="e.g. $297" value={section.price ?? ''} onChange={(v) => onChange({ price: v })} />
        </div>
        <TextField dynamic label="Price note" placeholder="e.g. one payment, or 3 x $109" value={section.price_note ?? ''}
          onChange={(v) => onChange({ price_note: v || undefined })} />
        <TextAreaField dynamic label="Fine print" value={section.body ?? ''} onChange={(v) => onChange({ body: v })} />
      </Block>
      <Block name="Media" defaultOpen>
        <ImageInput label="Product image" value={section.image_url ?? ''} onChange={(url) => onChange({ image_url: url })} />
      </Block>
      <Block name="Button" defaultOpen>
        <ButtonFields section={section} onChange={onChange} />
        <NoteField section={section} onChange={onChange} />
      </Block>
    </>
  );
}
